import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api, ErrorDeApi, type RegistroBitacora, type Usuario } from '../servicios/api'

const TAMANO = 100

/** Milisegundos de calma antes de consultar por el filtro de texto. */
const ESPERA_DE_TECLEO = 300

/**
 * Convierte el rango de fechas capturado (solo día) al rango UTC que espera la API. "Hasta
 * 19/08" tiene que incluir todo ese día: mandar `new Date('2026-08-19').toISOString()` es la
 * medianoche del 19, y el backend excluía en silencio todo lo registrado ese día (hallazgo
 * H-002, Alto, en un registro de auditoría). Ambos extremos se interpretan en hora local.
 */
export function rangoUtc(desde: string, hasta: string): { desdeUtc?: string; hastaUtc?: string } {
  return {
    desdeUtc: desde ? new Date(`${desde}T00:00:00.000`).toISOString() : undefined,
    hastaUtc: hasta ? new Date(`${hasta}T23:59:59.999`).toISOString() : undefined,
  }
}

/**
 * B7 · Las acciones que un auditor busca primero, resaltadas para que no haya que leer 100
 * renglones iguales hasta dar con ellas.
 *
 * No son «las destructivas»: son las que rompen una garantía que el sistema da por default.
 * Reabrir un periodo toca contabilidad que ya se declaró inmutable (RN-06); usar la e.firma
 * del cliente firma a su nombre; y las dos de plataforma son personal de ContaQuick tocando o
 * leyendo el despacho, que es justo lo que el consentimiento de soporte existe para acotar
 * (RF-107, §6.4). `Eliminar` entra por completitud: el repo no borra —archiva—, así que un
 * asiento con esa acción es de por sí algo que hay que mirar.
 *
 * `Archivar` NO entra: es la baja normal de un cliente y llenaría la pantalla de resaltado,
 * que es la forma de que el resaltado deje de significar nada.
 */
const ACCIONES_SENSIBLES = new Set([
  'ReabrirPeriodo',
  'Eliminar',
  'UsarEfirma',
  'AccionDePlataforma',
  'AccesoDeSoporteConConsentimiento',
])

export function esAccionSensible(accion: string): boolean {
  return ACCIONES_SENSIBLES.has(accion)
}

/**
 * B2: el prototipo presenta Bitácora y Usuarios como una sola sección, «Bitácora y usuarios».
 * Se conservan las dos rutas —romper una URL que alguien pudo guardar no vale lo que cuesta—
 * y cada pantalla pinta la barra con la otra.
 */
export function PestanasDeAdministracion({ activa }: { activa: 'bitacora' | 'usuarios' }) {
  return (
    <div className="pestanas" style={{ marginTop: 'var(--space-3)' }}>
      <Link className="pestana" to="/bitacora" aria-selected={activa === 'bitacora'} role="tab">
        Bitácora
      </Link>
      <Link className="pestana" to="/usuarios" aria-selected={activa === 'usuarios'} role="tab">
        Usuarios
      </Link>
    </div>
  )
}

/** Consulta y exportación de la bitácora de auditoría. SRS: RF-106, RNF-07. Cédula: I-04. */
export function Bitacora() {
  const [registros, setRegistros] = useState<RegistroBitacora[]>([])
  const [total, setTotal] = useState(0)
  const [pagina, setPagina] = useState(1)
  const [desde, setDesde] = useState('')
  const [hasta, setHasta] = useState('')
  const [entidad, setEntidad] = useState('')
  // B3: el endpoint ya aceptaba `usuarioId`; lo que faltaba era mandarlo. Un nombre que se ve
  // pero por el que no se puede filtrar deja el trabajo a medias en una tabla de 100 en 100.
  const [usuarioId, setUsuarioId] = useState('')
  const [usuarios, setUsuarios] = useState<Usuario[]>([])
  const [error, setError] = useState('')
  const [exportando, setExportando] = useState(false)

  // El filtro de texto disparaba una consulta por tecla, y sin cancelar: escribir «Poliza»
  // eran seis peticiones cuyas respuestas podían llegar desordenadas y pintar la equivocada.
  const [entidadAplicada, setEntidadAplicada] = useState('')
  useEffect(() => {
    const t = setTimeout(() => { setPagina(1); setEntidadAplicada(entidad) }, ESPERA_DE_TECLEO)
    return () => clearTimeout(t)
  }, [entidad])

  useEffect(() => {
    // La lista de usuarios solo alimenta el filtro: si falla, la bitácora sigue consultable.
    api.listarUsuarios().then(setUsuarios).catch(() => setUsuarios([]))
  }, [])

  useEffect(() => {
    let vigente = true
    async function recargar() {
      try {
        const respuesta = await api.bitacora({
          ...rangoUtc(desde, hasta),
          entidad: entidadAplicada || undefined,
          usuarioId: usuarioId || undefined,
          pagina,
          tamano: TAMANO,
        })
        if (!vigente) return
        setRegistros(respuesta.items)
        setTotal(respuesta.total)
        // Sin esto el error se quedaba pegado: la tabla y el contador se actualizaban con
        // datos buenos y encima seguía el mensaje rojo de la consulta anterior.
        setError('')
      } catch (e) {
        if (!vigente) return
        setError(e instanceof ErrorDeApi ? e.message : 'No se pudo cargar la bitácora.')
      }
    }
    void recargar()
    return () => { vigente = false }
  }, [pagina, desde, hasta, entidadAplicada, usuarioId])

  async function exportar() {
    setExportando(true)
    setError('')
    try {
      await api.exportarBitacora({
        ...rangoUtc(desde, hasta),
        entidad: entidadAplicada || undefined,
        usuarioId: usuarioId || undefined,
      })
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo exportar la bitácora.')
    } finally {
      setExportando(false)
    }
  }

  const totalPaginas = Math.max(1, Math.ceil(total / TAMANO))

  return (
    <section>
      <header className="encabezado">
        <h1>Bitácora y usuarios</h1>
        <span className="encabezado-acciones">
          <button onClick={() => void exportar()} disabled={exportando}>
            {exportando ? 'Exportando…' : 'Exportar bitácora'}
          </button>
        </span>
      </header>
      <p className="sutil">Auditoría por despacho · hora local · consultable por el administrador</p>

      <PestanasDeAdministracion activa="bitacora" />

      {/* `.acciones` empuja a la derecha: sirve para botones al pie de un panel, no para una
          fila de filtros, donde deja las etiquetas pegadas a sus campos. */}
      <div className="filtros" style={{ paddingTop: 'var(--space-3)' }}>
        <label>
          Desde
          <input type="date" value={desde} onChange={(e) => { setPagina(1); setDesde(e.target.value) }} />
        </label>
        <label>
          Hasta
          <input type="date" value={hasta} onChange={(e) => { setPagina(1); setHasta(e.target.value) }} />
        </label>
        <label>
          Entidad
          <input
            placeholder="Cliente, Poliza, Usuario…"
            value={entidad}
            onChange={(e) => setEntidad(e.target.value)}
          />
        </label>
        <label>
          Usuario
          <select value={usuarioId} onChange={(e) => { setPagina(1); setUsuarioId(e.target.value) }}>
            <option value="">Todos</option>
            {usuarios.map((u) => (
              <option key={u.id} value={u.id}>{u.nombre}</option>
            ))}
          </select>
        </label>
      </div>

      {error && <p className="error" role="alert">{error}</p>}

      <p className="sutil">Registros que coinciden con los filtros · {total} movimientos</p>

      <div className="tabla-envoltura">
      <table className="tabla">
        <thead>
          <tr>
            {/* B4 · Decía «Fecha y hora» a secas sobre un `toLocaleString`. En una bitácora
                de auditoría eso es un número sin procedencia: quien la coteje contra un acuse
                del SAT —que va en UTC— se equivoca por seis horas sin enterarse. El rótulo
                dice la zona y el `title` de cada celda lleva el instante UTC en ISO. */}
            <th>Fecha y hora (local)</th>
            <th>Usuario</th>
            <th>Entidad</th>
            <th>Acción</th>
            <th>Registro</th>
            <th>IP</th>
            <th>Detalle</th>
          </tr>
        </thead>
        <tbody>
          {registros.map((r) => (
            <tr key={r.id} className={esAccionSensible(r.accion) ? 'resaltada' : undefined}>
              <td className="sutil" style={{ whiteSpace: 'nowrap' }}
                title={`${r.fechaHoraUtc} (UTC)`}>
                {new Date(r.fechaHoraUtc).toLocaleString('es-MX')}
              </td>
              {/* B1 · Sin nombre, la pantalla que responde «quién hizo qué» no respondía la
                  mitad. Un asiento sin usuario no es un hueco: es el sistema, y se dice. */}
              <td>{r.usuarioNombre ?? <span className="sutil">sistema</span>}</td>
              <td>{r.entidadAfectada}</td>
              <td>{r.accion}</td>
              <td className="mono">{r.idRegistro}</td>
              <td className="mono">{r.direccionIp ?? '—'}</td>
              <td>{r.detalle}</td>
            </tr>
          ))}
          {registros.length === 0 && (
            <tr>
              <td colSpan={7} className="vacio">Sin movimientos que coincidan con estos filtros.</td>
            </tr>
          )}
        </tbody>
      </table>
      </div>

      {total > TAMANO && (
        <div className="acciones">
          <button disabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)}>Anterior</button>
          <span className="sutil">Página {pagina} de {totalPaginas}</span>
          <button disabled={pagina >= totalPaginas} onClick={() => setPagina((p) => p + 1)}>Siguiente</button>
        </div>
      )}

      <p className="nota">
        Quién creó, modificó, eliminó o envió qué y cuándo, con la IP de origen. La bitácora no
        se puede editar ni borrar desde la aplicación, y el CSV exportado lleva las mismas
        columnas que se ven aquí (RNF-07).
      </p>
    </section>
  )
}
